import { useEffect, useRef } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { Input } from 'components/atoms';
import { Select } from 'components/atoms/Select/Select.components';
import CountrySelect from 'components/molecules/CountrySelect/CountrySelect';
import { useGetPicklistQuery } from 'services/sap';
import { useUpdateAcademicInfoHiringMutation } from 'services/talento';
import { catalogoLenguage, catalogoPersonal } from 'utils/helpers/dictionaries';

import { IAdminPanelForm } from '../AdminPanelForm.schema';
import { FieldSet } from '../FieldSet/FieldSet';

import style from './AcademicDataSection.module.scss';

export function AcademicDataSection() {
  const { control, getValues, watch, setValue, clearErrors, trigger } = useFormContext<IAdminPanelForm>();
  const [updateAcademicInfoHiring] = useUpdateAcademicInfoHiringMutation();
  const { data: degreeDataRaw, isLoading: isLoadingDegree } = useGetPicklistQuery({
    catalogName: catalogoPersonal.degree,
    languages: [catalogoLenguage.ESyOptionId],
  });
  const degreeData = degreeDataRaw?.filter((v: any) => !v.status || v.status === 'A');
  const { data: yearData, isLoading: isLoadingYear } = useGetPicklistQuery({
    catalogName: catalogoPersonal.listYear,
    languages: [catalogoLenguage.ESyOptionId],
  });
  const { email } = useParams();

  const hasProfessionalCardOpts = [
    { label: 'Si', value: 'Si' },
    { label: 'No', value: 'No' },
    { label: 'No aplica', value: 'No aplica' },
    { label: 'Pendiente grados', value: 'Pendiente grados' },
  ]

  const hasProfessionalCard = watch('hasProfessionalCard');
  const professionalCard = watch('professionalCard');
  const graduationYear = watch('graduationYear');
  const semester = watch('semester');
  const initialRender = useRef(true);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }
    setValue('semester', '');
    setValue('graduationYear', '');
    clearErrors('semester');
    clearErrors('graduationYear');
    switch (hasProfessionalCard) {
      case 'No':
        setValue('professionalCard', 'Pendiente');
        break;
      case 'No aplica':
        setValue('professionalCard', 'No aplica');
        break;
      default:
        setValue('professionalCard', '');
        break;
    }
    clearErrors('professionalCard');
    submitData();
  }, [hasProfessionalCard]);

  useEffect(() => {
    if (hasProfessionalCard === 'Pendiente grados') {
      const value = `Grados ${graduationYear}-${semester ?? ''}`;
      setValue('professionalCard', value);
      clearErrors('professionalCard');
    }
  }, [graduationYear, semester]);

  useEffect(() => {
    trigger(['hasProfessionalCard', 'professionalCard', 'semester', 'graduationYear']);
  }, [hasProfessionalCard, professionalCard, semester, graduationYear]);

  async function submitData() {
    const values = getValues();
    const endDateRaw = values.endDate ?? '';
    const endDateFormatted = endDateRaw && endDateRaw !== 'EN CURSO'
      ? `${endDateRaw}-01-01`
      : '';

    await updateAcademicInfoHiring({
      ...values,
      email: email ?? '',
      endDate: endDateFormatted,
    } as any).unwrap();
  }

  return (
    <div className={style.AcademicDataSection}>
      <FieldSet title="informacion académica">
        <div className={style.AcademicDataSection__fields}>
          <div className={style.AcademicDataSection__grid}>
            <Controller
              control={control}
              name="educationalLevel"
              render={({
                field: { onBlur, value, ...field },
                fieldState: { error },
              }) => {
                const options = isLoadingDegree
                  ? [{ label: 'Cargando', value: 'Cargando' }]
                  : degreeData?.map((v: any) => ({ label: v.label_es_ES, value: v.label_es_ES })) ?? [];
                return <Select
                  onBlur={() => {
                    onBlur();
                    submitData();
                  }}
                  textLabel="Nivel educativo"
                  htmlForLabel="educationalLevel"
                  options={options}
                  handleChange={(val) => field.onChange(val)}
                  selected={value ?? ''}
                  error={error !== undefined}
                  disabled={isLoadingDegree}
                  subtext={error?.message}
                />;
              }}
            />
            <Controller
              control={control}
              name="professionalTitle"
              render={({
                field: { onBlur, ...field },
                fieldState: { error, isTouched },
              }) => (
                <Input
                  onBlur={(e) => {
                    onBlur();
                    submitData();
                  }}
                  id="professionalTitle"
                  label="Título profesional"
                  {...field}
                  error={error !== undefined}
                  success={isTouched && error === undefined}
                  subtext={error?.message}
                />
              )}
            />
          </div>
          <Controller
            control={control}
            name="institution"
            render={({
              field: { onBlur, ...field },
              fieldState: { error, isTouched },
            }) => (
              <Input
                onBlur={(e) => {
                  onBlur();
                  submitData();
                }}
                id="institution"
                label="Institución educativa"
                {...field}
                error={error !== undefined}
                success={isTouched && error === undefined}
                subtext={error?.message}
              />
            )}
          />
          <div className={style.AcademicDataSection__grid}>
            <Controller
              control={control}
              name="country"
              render={({
                field: { onBlur, value, ...field },
                fieldState: { error },
              }) => (
                <CountrySelect
                  textLabel='País'
                  htmlForLabel='country'
                  placeholder='Seleccione uno'
                  error={error !== undefined}
                  subtext={error?.message}
                  handleChange={(val) => field.onChange(val)}
                  onBlur={() => {
                    onBlur();
                    submitData();
                  }}
                  value={{
                    label: value as any,
                    value: value as any,
                  }}
                  {...field}
                  defaultValue={value}
                />
              )}
            />
            <Controller
              control={control}
              name="endDate"
              rules={{ required: false }}
              render={({
                field: { onBlur, value, onChange, ...field },
                fieldState: { error },
              }) => {
                const sortedYearOptions = (() => {
                  if (isLoadingYear) return [{ label: 'Cargando', value: 'Cargando' }];
                  if (!yearData) return [];
                  const enCurso = yearData.filter((v: any) => v.label_es_ES === 'EN CURSO');
                  const years = yearData
                    .filter((v: any) => v.label_es_ES !== 'EN CURSO')
                    .sort((a: any, b: any) => Number(b.label_es_ES) - Number(a.label_es_ES));
                  return [...enCurso, ...years].map((v: any) => ({ label: v.label_es_ES, value: v.label_es_ES }));
                })();
                return (
                  <Select
                    onBlur={() => {
                      onBlur();
                    }}
                    textLabel="Año de finalización (opcional)"
                    htmlForLabel="endDate"
                    placeholder="Seleccione uno"
                    options={sortedYearOptions}
                    handleChange={(val) => {
                      onChange(val);
                      submitData();
                    }}
                    selected={value ?? ''}
                    error={error !== undefined}
                    disabled={isLoadingYear}
                    subtext={error?.message}
                  />
                );
              }}
            />
          </div>
          <div className={style.AcademicDataSection__grid}>
            <Controller
              control={control}
              name="hasProfessionalCard"
              render={({
                field: { onBlur, ...field },
                fieldState: { error },
              }) => (
                <Select
                  onBlur={() => {
                    onBlur();
                    submitData();
                  }}
                  textLabel="¿Tiene tarjeta profesional?"
                  htmlForLabel="hasProfessionalCard"
                  options={hasProfessionalCardOpts}
                  handleChange={(val) => field.onChange(val)}
                  selected={field.value}
                  error={error !== undefined}
                  subtext={error?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="professionalCard"
              render={({
                field: { onBlur, ...field },
                fieldState: { error, isTouched },
              }) => (
                <Input
                  onBlur={(e) => {
                    onBlur();
                    submitData();
                  }}
                  placeholder="Ej: 05202349067ANT"
                  id="professionalCard"
                  label="Número de tarjeta profesional"
                  {...field}
                  disabled={hasProfessionalCard !== 'Si'}
                  error={error !== undefined}
                  success={isTouched && hasProfessionalCard === 'Si' && error === undefined}
                  subtext={error?.message}
                />
              )}
            />
            {hasProfessionalCard === 'Pendiente grados' && <>
              <Controller
                control={control}
                name="graduationYear"
                render={({
                  field: { onBlur, ...field },
                  fieldState: { error, isTouched },
                }) => (
                  <Input
                    type="number"
                    onBlur={(e) => {
                      onBlur();
                      submitData();
                    }}
                    placeholder="Ej: 2025"
                    id="graduationYear"
                    label="Año de grado"
                    {...field}
                    error={error !== undefined}
                    success={isTouched && hasProfessionalCard === 'Pendiente grados' && error === undefined}
                    subtext={error?.message}
                  />
                )}
              />
              <Controller
                control={control}
                name="semester"
                render={({
                  field: { onBlur, ...field },
                  fieldState: { error },
                }) => (
                  <Select
                    onBlur={() => {
                      onBlur();
                      submitData();
                    }}
                    textLabel="Semestre"
                    htmlForLabel="semester"
                    options={[
                      { label: '1', value: '1' },
                      { label: '2', value: '2' }
                    ]}
                    handleChange={(val) => field.onChange(val)}
                    selected={field.value}
                    error={error !== undefined}
                    subtext={error?.message}
                  />
                )}
              />
            </>}
          </div>
          <Controller
            control={control}
            name="cv"
            render={({
              field: { onBlur, ...field },
              fieldState: { error, isTouched },
            }) => (
              <Input
                onBlur={(e) => {
                  onBlur();
                  submitData();
                }}
                id="cv"
                label="Hoja de vida"
                {...field}
                error={error !== undefined}
                success={isTouched && error === undefined}
                subtext={error?.message}
              />
            )}
          />
        </div>
      </FieldSet>
    </div>
  );
}
